import {FluentNumber} from "@fluent/bundle";
import {Localized, useLocalization} from "@fluent/react";
import {
	Box,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import React, {useEffect, useState} from "react";

import Widget from "../analytics/widget/Widget";
import WidgetBody from "../analytics/widget/WidgetBody";
import WidgetTitle from "../analytics/widget/WidgetTitle";
import describeStudyTarget from "../../store/courses/describeStudyTarget";
import selectAvailableExerciseTypes from "../../store/courses/selectAvailableExerciseTypes";
import type StudyTargetProgress from "../../store/courses/StudyTargetProgress";
import Feature from "../../store/features/Feature";
import useFeatureEnabled from "../../store/features/useFeatureEnabled";
import type FetchStatus from "../../store/FetchStatus";
import {useAppSelector} from "../../store/hooks";
import {studentService} from "../../store/services/studentService";
import LoadingError from "../../utils/errors/LoadingError";
import ProgressBar from "../../utils/ProgressBar";
import randomN from "../../utils/randomN";

function StudyTargetsWidget(props: {
	id: string;
	title: React.ReactNode;
	studentId: number;
	courseId: number;
}) {
	return (
		<Widget id={props.id}>
			<WidgetTitle>{props.title}</WidgetTitle>
			<WidgetBody>
				<StudyTargets studentId={props.studentId} courseId={props.courseId} />
			</WidgetBody>
		</Widget>
	);
}

function StudyTargets(props: {studentId: number; courseId: number}) {
	const [targets, setTargets] = useState<StudyTargetProgress[]>([]);

	const [fetchStatus, setFetchStatus] = useState<FetchStatus>("none");

	const [featureEnabled] = useFeatureEnabled();

	const numberOfExerciseTypes = useAppSelector(
		(state) =>
			selectAvailableExerciseTypes(
				state,
				props.courseId,
				featureEnabled(Feature.ExternalExercises)
			).length
	);

	async function fetchTargets(studentId: number, courseId: number) {
		setFetchStatus("pending");

		try {
			const targets = await studentService.getStudyTargetProgress(
				studentId,
				courseId
			);

			setTargets(targets);
			setFetchStatus("succeeded");
		} catch {
			setFetchStatus("failed");
		}
	}

	useEffect(() => {
		fetchTargets(props.studentId, props.courseId);
	}, [props.courseId, props.studentId]);

	const noTargets = fetchStatus === "succeeded" && targets.length === 0;

	return (
		<Stack sx={{width: 1, position: "relative"}}>
			{noTargets && <EmptyState />}

			{fetchStatus === "failed" && (
				<ErrorState
					onReload={() => fetchTargets(props.studentId, props.courseId)}
				/>
			)}

			<Table>
				<TableHead>
					<TableRow>
						<TableCell sx={{width: "50%"}}>
							<Localized id="my-corner-study-targets-widget-label-target">
								Target
							</Localized>
						</TableCell>
						<TableCell>
							<Localized id="my-corner-study-targets-widget-label-progress">
								Progress
							</Localized>
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{(fetchStatus !== "succeeded" || targets.length === 0) && (
						<>
							<TargetRowPlaceholder />
							<TargetRowPlaceholder />
							<TargetRowPlaceholder />
						</>
					)}

					{targets.map((t, i) => (
						<TargetRow
							key={i}
							target={t}
							numberOfExerciseTypes={numberOfExerciseTypes}
						/>
					))}
				</TableBody>
			</Table>
		</Stack>
	);
}

function TargetRow(props: {
	target: StudyTargetProgress;
	numberOfExerciseTypes: number;
}) {
	const {target} = props;

	const {l10n} = useLocalization();

	const description = describeStudyTarget(
		l10n,
		props.numberOfExerciseTypes,
		target
	);

	return (
		<TableRow hover>
			<TableCell>{description}</TableCell>
			<TableCell>
				<ProgressBar
					value={target.progress}
					theme="secondary"
					leftLabel={
						<Localized
							id="my-corner-study-targets-widget-progress-value"
							vars={{
								value: new FluentNumber(target.progress, percentFormat),
							}}
						>
							{`${target.progress}%`}
						</Localized>
					}
				/>
			</TableCell>
		</TableRow>
	);
}

const percentFormat = {maximumFractionDigits: 2};

function TargetRowPlaceholder() {
	return (
		<TableRow>
			<TableCell>
				<Skeleton width={`${randomN(30, 100)}%`} />
			</TableCell>
			<TableCell>
				<ProgressBar theme="grey" leftLabel={<Skeleton width="4ch" />} />
			</TableCell>
		</TableRow>
	);
}

function EmptyState() {
	return (
		<StateContainer>
			<Typography variant="h6">
				<Localized id="my-corner-study-targets-widget-no-results-title">
					No targets
				</Localized>
			</Typography>
		</StateContainer>
	);
}

function ErrorState(props: {onReload: () => void}) {
	return (
		<StateContainer>
			<LoadingError
				variant="block"
				description={
					<Localized id="my-corner-study-targets-widget-loading-error-desc">
						Something has gone wrong, and we cannot load study targets
					</Localized>
				}
				onReload={props.onReload}
			/>
		</StateContainer>
	);
}

function StateContainer(props: {children: NonNullable<React.ReactNode>}) {
	return (
		<Box
			sx={{
				display: "flex",
				position: "absolute",
				inset: 0,
				justifyContent: "center",
				alignItems: "center",
				background: (theme) => theme.palette.background.paper,
				zIndex: 1,
			}}
		>
			{props.children}
		</Box>
	);
}

export default StudyTargetsWidget;
